import { computed, inject, Injectable, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import {
  isStorageAvailable,
  storageGet,
  storageRemove,
  storageSet,
} from "../utils/safe-storage";

@Injectable({ providedIn: "root" })
export class ImpersonationService {
  private readonly http = inject(HttpClient);
  private readonly STORAGE_KEY = "impersonation_token";

  /** Single read at construction, seeding both the signal and the fallback. */
  private readonly initialToken = storageGet(this.STORAGE_KEY);

  readonly token = signal<string | null>(this.initialToken);
  readonly isActive = computed<boolean>(() => !!this.token());
  /** Display name of the impersonated user (set after /me by the landing route). */
  readonly targetName = signal<string>("");
  /** In-memory fallback when Web Storage is blocked (#622). Mirrors
   *  AuthTokenService.memoryToken: plain field so getToken() stays non-tracking,
   *  seeded from the construction-time read so a storage failure MID-session
   *  cannot drop a token inherited from a previous one. That matters most here:
   *  the interceptor's 401 cleanup gates on getToken(), so a lost impersonation
   *  token never gets cleared — the session stays "logged in" while every
   *  request 401s. */
  private memoryToken: string | null = this.initialToken;

  getToken(): string | null {
    // Read localStorage directly (NOT the signal) so the interceptor doesn't
    // create a reactive dependency. Mirrors AuthTokenService.getToken().
    const fromStorage = storageGet(this.STORAGE_KEY);
    return isStorageAvailable() ? fromStorage : this.memoryToken;
  }

  enter(token: string) {
    this.memoryToken = token;
    storageSet(this.STORAGE_KEY, token);
    this.token.set(token);
  }

  clear() {
    this.memoryToken = null;
    storageRemove(this.STORAGE_KEY);
    this.token.set(null);
    this.targetName.set("");
  }

  /** Revoke the session server-side. The interceptor attaches the Impersonate
   *  header from the still-present token, so this runs as the impersonated user. */
  exit() {
    return this.http.post(`/api/v1/events/impersonation/exit/`, {});
  }
}
